// Load matching tab (ST-00218 P2): the sign-off form for one route or a batch (SPEC §5, Q5 =
// per route with batch select). One POST /signoffs writes one record per route with a shared
// batch id and a JSON snapshot each; the signable document is the batch export.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { FaFileCsv, FaFilePdf, FaTimes } from 'react-icons/fa';
import {
  downloadBatchExport,
  loadMatchingErrorText,
  signOff,
  type ExportFormat,
} from '../../../../services/loadMatching';
import type { SemanticChange, SignoffCreateResponse } from '../../../../types/loadMatching';
import { fmtPeriod } from './format';

export interface SignoffDialogProps {
  routes: { groupingId: number; name: string }[];
  period: { startDate: string; endDate: string };
  semanticChanges: SemanticChange[];
  onClose: () => void;
  /** Called after a successful sign-off (the caller refreshes its figures). */
  onSigned: (res: SignoffCreateResponse) => void;
}

const inputCls =
  'w-full px-3 py-2 border border-slate-300 rounded-lg bg-surface text-sm text-slate-900 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15';

export default function SignoffDialog({
  routes,
  period,
  semanticChanges,
  onClose,
  onSigned,
}: SignoffDialogProps) {
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<SignoffCreateResponse | null>(null);
  const [downloading, setDownloading] = useState<ExportFormat | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const many = routes.length > 1;
  const canSubmit = name.trim().length > 0 && !busy;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await signOff({
        groupingIds: routes.map((r) => r.groupingId),
        startDate: period.startDate,
        endDate: period.endDate,
        signedByName: name.trim(),
        signedByTitle: title.trim() || null,
        note: note.trim() || null,
      });
      setDone(res);
      onSigned(res);
    } catch (err) {
      setError(loadMatchingErrorText(err, 'The sign-off could not be saved. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const download = async (format: ExportFormat) => {
    if (!done) return;
    setDownloading(format);
    setError(null);
    try {
      await downloadBatchExport(done.batchId, format);
    } catch (err) {
      setError(loadMatchingErrorText(err, 'The document could not be downloaded.'));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lm-signoff-title"
        className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-surface rounded-t-xl sm:rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 id="lm-signoff-title" className="text-base font-bold text-slate-900">
              {done
                ? 'Signed off'
                : many
                  ? `Sign off ${routes.length} routes`
                  : `Sign off ${routes[0]?.name ?? 'route'}`}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Period {fmtPeriod(period)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 mt-1"
          >
            <FaTimes size={16} />
          </button>
        </div>

        {done ? (
          <div className="px-5 py-4 space-y-4">
            <p className="text-sm text-slate-700">
              {done.signoffs.length === 1
                ? `${done.signoffs[0]?.groupingName} is signed off.`
                : `${done.signoffs.length} routes are signed off.`}{' '}
              Download the signed document to keep with the customer&apos;s file. Signing off
              records the review; it does not change any route&apos;s matching.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => download('pdf')}
                disabled={downloading !== null}
                className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                <FaFilePdf className="w-3.5 h-3.5" />
                {downloading === 'pdf' ? 'Preparing…' : 'Signed document (PDF)'}
              </button>
              <button
                type="button"
                onClick={() => download('csv')}
                disabled={downloading !== null}
                className="inline-flex items-center gap-1.5 h-9 px-3 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                <FaFileCsv className="w-3.5 h-3.5" />
                {downloading === 'csv' ? 'Preparing…' : 'CSV'}
              </button>
            </div>
            {error && <p className="text-xs text-red-700">{error}</p>}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="h-9 px-4 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="px-5 py-4 space-y-4">
            <div className="text-sm text-slate-700 space-y-2">
              <p>
                You confirm that you reviewed the current and the new figures, the changed loads and
                the transit window of {many ? 'these routes' : 'this route'} for this period.
                Signing off is optional: it records the review for the customer&apos;s file and
                changes nothing on the route.
              </p>
              {many && (
                <ul className="max-h-32 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 space-y-0.5">
                  {routes.map((r) => (
                    <li key={r.groupingId}>{r.name}</li>
                  ))}
                </ul>
              )}
            </div>
            {semanticChanges.length > 0 && (
              <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2">
                <p className="text-[11px] font-semibold text-sky-900 mb-1">
                  What changes with the new matching
                </p>
                <ol className="list-decimal pl-4 text-[11px] text-sky-900 space-y-0.5">
                  {semanticChanges.map((c) => (
                    <li key={c.code}>{c.text}</li>
                  ))}
                </ol>
              </div>
            )}
            <div>
              <label
                htmlFor="lm-signoff-name"
                className="block text-xs font-medium text-slate-600 mb-1"
              >
                Name of the person signing <span className="text-red-600">*</span>
              </label>
              <input
                id="lm-signoff-name"
                ref={nameRef}
                value={name}
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
                className={inputCls}
                autoComplete="name"
                required
              />
            </div>
            <div>
              <label
                htmlFor="lm-signoff-jobtitle"
                className="block text-xs font-medium text-slate-600 mb-1"
              >
                Job title
              </label>
              <input
                id="lm-signoff-jobtitle"
                value={title}
                maxLength={120}
                onChange={(e) => setTitle(e.target.value)}
                className={inputCls}
                placeholder="e.g. Logistics Manager"
                autoComplete="organization-title"
              />
            </div>
            <div>
              <label
                htmlFor="lm-signoff-note"
                className="block text-xs font-medium text-slate-600 mb-1"
              >
                Note
              </label>
              <textarea
                id="lm-signoff-note"
                value={note}
                maxLength={2000}
                rows={3}
                onChange={(e) => setNote(e.target.value)}
                className={inputCls}
                placeholder="e.g. Reviewed with the weighbridge team"
              />
            </div>
            {error && (
              <p role="alert" className="text-xs text-red-700">
                {error}
              </p>
            )}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="h-9 px-4 text-xs font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="h-9 px-4 text-xs font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:bg-slate-200 disabled:text-slate-400"
              >
                {busy ? 'Signing off…' : many ? `Sign off ${routes.length} routes` : 'Sign off'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
